
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PEOPLE } from '../data/GET_PEOPLE';

/** `?tags=3,7` — kept in the URL alongside `search` so a filtered list is linkable. */
const TAGS_PARAM = 'tags';

/**
 * `?missingConsent=1` — the registration follow-up worklist.
 *
 * In the URL for the same reason as the others: this list is something a centre manager works
 * through over days and will want to bookmark or send to someone.
 */
const MISSING_CONSENT_PARAM = 'missingConsent';

const parseTagIds = (params: URLSearchParams): number[] =>
    (params.get(TAGS_PARAM) ?? '')
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));

const parseMissingConsent = (params: URLSearchParams): boolean =>
    params.get(MISSING_CONSENT_PARAM) === '1';

function usePeopleSearch() {
    const navigate = useNavigate();
    const location = useLocation();
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get('search') || ''
    );
    const [tagIds, setTagIds] = useState<number[]>(
        () => parseTagIds(new URLSearchParams(location.search))
    );
    const [missingConsent, setMissingConsent] = useState<boolean>(
        () => parseMissingConsent(new URLSearchParams(location.search))
    );
    const [hasSearched, setHasSearched] = useState(false);
    const { loading, error, data, refetch } = useQuery(GET_PEOPLE, {
        variables: { search, tagIds, missingConsent },
    });
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Every filter lives in one query string, so any one of them changing has to carry the rest.
    const buildSearchString = useCallback(
        (nextSearch: string, nextTagIds: number[], nextMissingConsent: boolean) => {
            const params = new URLSearchParams();
            params.set('search', nextSearch);
            if (nextTagIds.length > 0) params.set(TAGS_PARAM, nextTagIds.join(','));
            if (nextMissingConsent) params.set(MISSING_CONSENT_PARAM, '1');
            return `?${params.toString()}`;
        },
        []
    );

    const handleSearchChange = useCallback((_: any, change: any) => {
        if (!hasSearched) setHasSearched(true);
        clearTimeout(timeoutRef.current);
        setSearch(change.value);
        timeoutRef.current = setTimeout(() => {
            navigate({
                pathname: location.pathname,
                search: buildSearchString(change.value, tagIds, missingConsent),
            });
            refetch({ search: change.value, tagIds, missingConsent });
        }, 500);
    }, [refetch, hasSearched, navigate, location.pathname, tagIds, missingConsent, buildSearchString]);

    // Picking from a dropdown is a deliberate act, not typing — so no debounce here; the
    // 500ms wait that stops a keystroke-per-request would just feel like lag.
    const handleTagsChange = useCallback((nextTagIds: number[]) => {
        clearTimeout(timeoutRef.current);
        setTagIds(nextTagIds);
        navigate({
            pathname: location.pathname,
            search: buildSearchString(search, nextTagIds, missingConsent),
        });
        refetch({ search, tagIds: nextTagIds, missingConsent });
    }, [refetch, navigate, location.pathname, search, missingConsent, buildSearchString]);

    const handleMissingConsentChange = useCallback((next: boolean) => {
        clearTimeout(timeoutRef.current);
        setMissingConsent(next);
        navigate({
            pathname: location.pathname,
            search: buildSearchString(search, tagIds, next),
        });
        refetch({ search, tagIds, missingConsent: next });
    }, [refetch, navigate, location.pathname, search, tagIds, buildSearchString]);

    // Sync state with URL on browser back/forward
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const param = params.get('search');
        const urlTagIds = parseTagIds(params);
        const urlMissingConsent = parseMissingConsent(params);
        const tagsDiffer = urlTagIds.join(',') !== tagIds.join(',');
        const consentDiffers = urlMissingConsent !== missingConsent;

        if (param === null) {
            setSearch('');
            setHasSearched(false);
            if (tagsDiffer) setTagIds(urlTagIds);
            if (consentDiffers) setMissingConsent(urlMissingConsent);
            refetch({ search: '', tagIds: urlTagIds, missingConsent: urlMissingConsent });
        } else if (param !== search || tagsDiffer || consentDiffers) {
            setSearch(param);
            setHasSearched(true);
            if (tagsDiffer) setTagIds(urlTagIds);
            if (consentDiffers) setMissingConsent(urlMissingConsent);
            refetch({ search: param, tagIds: urlTagIds, missingConsent: urlMissingConsent });
        }
    }, [location.search, refetch]);

    const hasTagFilter = tagIds.length > 0;

    // A tag filter can legitimately return few or no people, and the search box has to stay
    // on screen while it does — otherwise narrowing to an empty result removes the control
    // you need to widen it again. The consent filter has exactly the same property.
    const shouldShowSearch =
        (data?.users.length > 10) || search.length > 0 || hasSearched || hasTagFilter || missingConsent;

    return {
        search,
        tagIds,
        missingConsent,
        hasTagFilter,
        loading,
        error,
        data,
        handleSearchChange,
        handleTagsChange,
        handleMissingConsentChange,
        shouldShowSearch,
    };
}

export { usePeopleSearch };
